import { useState, useEffect } from 'react'
import { ScrollReveal } from '@/components/ui/scroll-reveal'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { getTestimonials, Testimonial } from '@/services/content'
import useLanguageStore from '@/stores/languageStore'
import { Quote, Star, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'

interface SectorTestimonialsSectionProps {
  sectorId: string
  sectorTitle: string
}

export const SectorTestimonialsSection = ({
  sectorId,
  sectorTitle,
}: SectorTestimonialsSectionProps) => {
  const { currentLocale } = useLanguageStore()
  const [testimonials, setTestimonials] = useState<Testimonial[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchTestimonials = async () => {
      setLoading(true)
      const { data } = await getTestimonials(currentLocale, sectorId)
      if (data) setTestimonials(data)
      setLoading(false)
    }
    fetchTestimonials()
  }, [currentLocale, sectorId])

  if (!loading && testimonials.length === 0) return null

  return (
    <section className="py-24 bg-slate-50 border-t border-slate-200">
      <div className="container px-4 md:px-6">
        <ScrollReveal className="text-center mb-12 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            O que dizem os clientes de {sectorTitle}
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Veja como empresas do seu setor estão a transformar as suas
            operações com a nossa tecnologia.
          </p>
        </ScrollReveal>

        <div className="max-w-7xl mx-auto px-4 sm:px-12">
          {loading ? (
            <div className="grid md:grid-cols-3 gap-8">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="h-64 bg-white rounded-xl animate-pulse shadow-sm border border-slate-100"
                />
              ))}
            </div>
          ) : (
            <ScrollReveal delay={200}>
              <Carousel
                opts={{
                  align: 'start',
                  loop: true,
                }}
                className="w-full"
              >
                <CarouselContent className="-ml-4 pb-4">
                  {testimonials.map((item) => (
                    <CarouselItem
                      key={item.id}
                      className="pl-4 md:basis-1/2 lg:basis-1/3 h-full"
                    >
                      <div className="h-full p-1">
                        <Card className="h-full border-none shadow-sm hover:shadow-md transition-shadow duration-300 bg-white flex flex-col">
                          <CardHeader className="pb-4">
                            <div className="flex gap-1 mb-4">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Star
                                  key={i}
                                  className={cn(
                                    'w-4 h-4',
                                    i < item.rating
                                      ? 'text-amber-400 fill-amber-400'
                                      : 'text-slate-200 fill-slate-200',
                                  )}
                                />
                              ))}
                            </div>
                            <Quote className="w-8 h-8 text-primary/20 mb-2" />
                          </CardHeader>
                          <CardContent className="flex-1 flex flex-col justify-between pt-0 pb-8 px-8">
                            <p className="text-slate-600 leading-relaxed italic mb-8 line-clamp-6">
                              "{item.content}"
                            </p>

                            <div className="flex items-center gap-4 mt-auto">
                              <Avatar className="h-12 w-12 border-2 border-slate-100">
                                <AvatarImage
                                  src={item.author_avatar_url || ''}
                                  alt={item.author_name}
                                />
                                <AvatarFallback className="bg-slate-100 text-slate-400">
                                  <User className="w-6 h-6" />
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-bold text-slate-900 text-sm">
                                  {item.author_name}
                                </p>
                                {item.author_role && (
                                  <p className="text-slate-500 text-xs">
                                    {item.author_role}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <div className="hidden md:block">
                  <CarouselPrevious className="-left-12 lg:-left-16 border-slate-200 hover:bg-white hover:text-primary" />
                  <CarouselNext className="-right-12 lg:-right-16 border-slate-200 hover:bg-white hover:text-primary" />
                </div>
              </Carousel>
            </ScrollReveal>
          )}
        </div>
      </div>
    </section>
  )
}
